import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Building2, Loader2, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Hero */}
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1776617130025-df2d5288107a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBjb3Jwb3JhdGUlMjBidWlsZGluZyUyMGdsYXNzJTIwZmFjYWRlfGVufDB8fHx8MTc3Nzg4NTcyM3ww&ixlib=rb-4.1.0&q=85"
          className="absolute inset-0 w-full h-full object-cover"
          alt="Modern corporate building"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E40AF]/80 via-[#3B82F6]/50 to-black/30" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/15 backdrop-blur grid place-items-center">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <div className="font-heading font-semibold text-lg">United Group</div>
              <div className="text-xs uppercase tracking-widest opacity-80">Recruitment Management</div>
            </div>
          </div>

          <div className="space-y-6 max-w-md">
            <div className="text-[11px] uppercase tracking-[0.25em] opacity-70">FY 2025–26</div>
            <h1 className="font-heading text-4xl xl:text-5xl font-semibold leading-[1.05] tracking-tight">
              Hire smarter. <br />
              <span className="opacity-80">Track every position.</span>
            </h1>
            <p className="text-sm opacity-80 leading-relaxed">
              An ATS-class system for UOSPL, UAFPL & UAPL —
              positions, attachments, analytics and automation in one place.
            </p>
          </div>

          <div className="flex items-center gap-6 text-xs opacity-70">
            <span>UAFPL · UAPL · UOSPL</span>
            <span className="font-mono">v1.0</span>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md animate-slide-up">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-lg bg-primary text-primary-foreground grid place-items-center">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <div className="font-heading font-semibold text-lg">United Group</div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">RMS</div>
            </div>
          </div>

          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
            Sign in to continue
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mb-2">
            Welcome back.
          </h2>
          <p className="text-muted-foreground text-sm mb-10">
            Use your United Group credentials to access the recruitment console.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-muted-foreground">
                Email address
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@united-group.in"
                required
                className="mt-2 h-11"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs uppercase tracking-widest text-muted-foreground">
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-xs text-primary hover:underline font-medium"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-2 h-11"
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-11 mt-2"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ArrowRight className="w-4 h-4 mr-2" />
              )}
              Sign in
            </Button>
          </form>

          <p className="text-xs text-muted-foreground mt-10 leading-relaxed">
            Trouble signing in? Reach out to your administrator at{" "}
            <span className="text-foreground font-medium">hrd@united-group.in</span>.
          </p>
        </div>
      </div>
    </div>
  );
}
